import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Divider,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import apiService from '../../services/apiService';

const ClientDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [client, setClient] = useState(null);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchClientData = async () => {
      try {
        setLoading(true);
        const clientData = await apiService.getClient(id);
        setClient(clientData);
        
        // Cargar la información de ubicación
        if (clientData.location_ubigeo) {
          const locationData = await apiService.getLocationUbigeo(clientData.location_ubigeo);
          setLocation(locationData);
        }
      } catch (err) {
        console.error('Error al cargar datos del cliente:', err);
        setError('Error al cargar los datos del cliente. Por favor, intente nuevamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchClientData();
  }, [id]);

  const handleBack = () => {
    navigate('/clients');
  };

  const handleEdit = () => {
    navigate(`/clients/edit/${id}`);
  };

  const getDocumentTypeLabel = (type) => {
    switch (type) {
      case 'DNI':
        return 'DNI';
      case 'CE':
        return 'Carnet de Extranjería';
      case 'PAS':
        return 'Pasaporte';
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Box display="flex" alignItems="center" mb={3}>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mr: 2 }}>
            Volver
          </Button>
          <Typography variant="h4">Detalles del Cliente</Typography>
        </Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!client) {
    return (
      <Box>
        <Box display="flex" alignItems="center" mb={3}>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mr: 2 }}>
            Volver
          </Button>
          <Typography variant="h4">Detalles del Cliente</Typography>
        </Box>
        <Alert severity="warning">No se encontró el cliente solicitado.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mr: 2 }}>
            Volver
          </Button>
          <Typography variant="h4">Detalles del Cliente</Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          onClick={handleEdit}
        >
          Editar
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" mb={2}>
              <PersonIcon color="primary" sx={{ fontSize: 30, mr: 1 }} />
              <Typography variant="h5">
                {`${client.first_name} ${client.father_last_name} ${client.mother_last_name}`}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" color="textSecondary" gutterBottom>
              Información Personal
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Tipo de Documento
              </Typography>
              <Typography variant="body1">
                {getDocumentTypeLabel(client.document_type)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="textSecondary">
                Número de Documento
              </Typography>
              <Chip 
                label={client.document_number} 
                color="primary" 
                variant="outlined" 
                size="small" 
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" color="textSecondary" gutterBottom>
              Información de Contacto
            </Typography>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body1">
                {`${client.phone_prefix} ${client.phone_number}`}
              </Typography>
            </Box>
            {location && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <LocationIcon fontSize="small" sx={{ mr: 1, mt: 0.5, color: 'text.secondary' }} />
                <Typography variant="body1">
                  {`${location.department_name}, ${location.province_name}, ${location.district_name}`}
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box display="flex" justifyContent="space-between">
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Creado el
                </Typography>
                <Typography variant="body2">
                  {new Date(client.created_at).toLocaleString()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Última actualización
                </Typography>
                <Typography variant="body2">
                  {new Date(client.updated_at).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default ClientDetail; 